/**
 * Codex event parser
 *
 * Converts raw or normalized Codex provider events into MessageGroup[] for rendering.
 * Maps Codex items to existing group types:
 *   - reasoning  → ThinkingGroup
 *   - agent_message → TextGroup
 *   - command_execution → BashGroup
 *   - turn.completed → ResultGroup
 *   - turn.failed / error → ResultGroup (error)
 */

import type {
    CodexCommandExecutionItem,
    CodexErrorEvent,
    CodexEvent,
    CodexFileChangeItem,
    CodexItem,
    CodexOrchestrationItem,
    CodexProviderNormalizationStats,
    CodexRawJsonEvent,
    CodexTurnCompletedEvent,
    CodexTurnFailedEvent,
} from "../../../providerEvents/codex"
import { normalizeCodexProviderEvents } from "../../../providerEvents/codex"
import { type CodexStaticValue, parseCodexOrchestration } from "../../../providerEvents/codexOrchestration"
import type { BashGroup, MessageGroup, ProviderNoticeGroup, TodoItem, ToolGroup } from "../messageGroups"
import { extractCodexAgentActivity } from "./agentActivity"

type RenderableCodexItemType = Exclude<CodexItem["type"], "unsupported">

const CODEX_ITEM_RENDER_POLICIES = {
    reasoning: "thinking",
    agent_message: "text",
    command_execution: "bash",
    orchestration: "orchestration",
    custom_tool_call: "tool",
    file_change: "fileChange",
    mcp_tool_call: "tool",
    web_search: "tool",
    todo_list: "todoWrite",
    error: "error",
} satisfies Record<RenderableCodexItemType, "thinking" | "text" | "bash" | "orchestration" | "fileChange" | "tool" | "todoWrite" | "error">

/**
 * Group Codex provider events into MessageGroups for inline rendering.
 *
 * Prefers `item.completed` over `item.started` for the same item ID
 * (completed has final output, exit code, etc.).
 */
export function groupCodexProviderEvents(messages: unknown[], completionUsage?: { costUsd?: number; durationMs?: number }): MessageGroup[] {
    const normalized = normalizeCodexProviderEvents(messages)
    const codexEvents = normalized.events

    const groups: MessageGroup[] = []
    const agentActivity = extractCodexAgentActivity(codexEvents)

    // Track which item IDs we've seen via item.completed so we can skip
    // their item.started counterparts.
    const completedItemIds = new Set<string>()

    // First pass: collect completed item IDs
    for (const msg of codexEvents) {
        if (msg.type === "item.completed") {
            completedItemIds.add(msg.item.id)
        }
    }

    // Second pass: build groups
    for (let i = 0; i < codexEvents.length; i++) {
        const msg = codexEvents[i]

        if (agentActivity.group?.messageIndex === i) {
            groups.push(agentActivity.group)
        }
        if (agentActivity.consumedIndices.has(i)) continue

        const webSearchGroup = getWebSearchToolGroup(msg, i)
        if (webSearchGroup) {
            groups.push(webSearchGroup)
            continue
        }

        // item.started — only process if no corresponding item.completed exists
        if (msg.type === "item.started") {
            if (completedItemIds.has(msg.item.id)) continue
            pushItemGroup(groups, msg.item, i, true)
            continue
        }

        if (msg.type === "item.updated") {
            if (completedItemIds.has(msg.item.id)) continue
            pushItemGroup(groups, msg.item, i, false)
            continue
        }

        // item.completed — always process
        if (msg.type === "item.completed") {
            pushItemGroup(groups, msg.item, i, false)
            continue
        }

        // turn.completed — result with usage
        if (msg.type === "turn.completed") {
            const tc = msg as CodexTurnCompletedEvent
            groups.push({
                type: "result",
                subtype: "success",
                durationMs: completionUsage?.durationMs ?? 0,
                totalCostUsd: completionUsage?.costUsd ?? 0,
                usage: {
                    inputTokens: tc.usage.input_tokens,
                    outputTokens: tc.usage.output_tokens,
                    cacheReadTokens: tc.usage.cached_input_tokens,
                },
                isError: false,
                messageIndex: i,
            })
            continue
        }

        // turn.failed — error result
        if (msg.type === "turn.failed") {
            const tf = msg as CodexTurnFailedEvent
            groups.push({
                type: "result",
                subtype: "error_during_execution",
                durationMs: 0,
                totalCostUsd: 0,
                usage: { inputTokens: 0, outputTokens: 0 },
                isError: true,
                errors: [tf.error.message ?? "Turn failed"],
                messageIndex: i,
            })
            continue
        }

        // error — top-level error
        if (msg.type === "error") {
            const err = msg as CodexErrorEvent
            groups.push({
                type: "result",
                subtype: "error_during_execution",
                durationMs: 0,
                totalCostUsd: 0,
                usage: { inputTokens: 0, outputTokens: 0 },
                isError: true,
                errors: [err.message],
                messageIndex: i,
            })
            continue
        }

        // thread.started — session init pill
        if (msg.type === "thread.started") {
            const { type: _type, ...metadata } = msg as unknown as Record<string, unknown>
            groups.push({
                type: "system",
                subtype: "init",
                metadata,
                messageIndex: i,
            })
            continue
        }

        if (msg.type === "raw_json") {
            pushCodexRawJsonGroup(groups, msg, i)
            continue
        }

        // turn.started — no visual representation
    }

    logNativeCodexNormalization(normalized.stats, groups.length)
    assignSourceMessageIndices(groups, normalized.sourceIndices)
    return groups
}

function assignSourceMessageIndices(groups: MessageGroup[], sourceIndices: readonly number[]): void {
    for (const group of groups) {
        const index =
            group.type === "tool" ||
            group.type === "edit" ||
            group.type === "write" ||
            group.type === "bash" ||
            group.type === "orchestration" ||
            group.type === "todoWrite"
                ? group.messageIndices[0]
                : group.type === "stderr"
                  ? -1
                  : group.messageIndex
        if (index >= 0) {
            Object.defineProperty(group, "sourceMessageIndex", {
                configurable: true,
                value: sourceIndices[index] ?? index,
            })
        }
    }
}

function logNativeCodexNormalization(stats: CodexProviderNormalizationStats | undefined, groupCount: number): void {
    if (!stats) return
    const payload = {
        nativeCount: stats.nativeCount,
        normalizedCount: stats.normalizedCount,
        skippedCount: stats.skippedCount,
        groupCount,
        typeCounts: stats.typeCounts,
    }
    if (isCodexParserTraceEnabled()) {
        const message = groupCount === 0 ? "[Codex] native replay produced no renderable groups" : "[Codex] normalized native replay"
        console.info(message, payload)
    }
}

function isCodexParserTraceEnabled(): boolean {
    try {
        return typeof globalThis.localStorage !== "undefined" && globalThis.localStorage.getItem("wildlark:codex:traceParser") === "1"
    } catch {
        return false
    }
}

function getWebSearchToolGroup(msg: CodexEvent, messageIndex: number): ToolGroup | null {
    const event = msg as unknown as { type?: unknown; original_type?: unknown; raw?: unknown }
    let raw: Record<string, unknown> | null = null

    if (event.type === "web_search") {
        raw = event as Record<string, unknown>
    } else if (event.type === "raw_json" && event.original_type === "web_search" && event.raw && typeof event.raw === "object") {
        raw = event.raw as Record<string, unknown>
    }

    if (!raw) return null

    return buildWebSearchToolGroup(raw, messageIndex)
}

function buildWebSearchToolGroup(raw: Record<string, unknown>, messageIndex: number): ToolGroup {
    const { type: _type, id: rawId, ...input } = raw
    const toolUseId = typeof rawId === "string" ? rawId : `web-search-${messageIndex}`
    return {
        type: "tool",
        toolUseId,
        toolName: "WebSearch",
        input,
        isError: false,
        messageIndices: [messageIndex, undefined],
    }
}

function buildMcpToolCallGroup(raw: Record<string, unknown>, messageIndex: number): ToolGroup {
    const server = typeof raw.server === "string" ? raw.server : "mcp"
    const tool = typeof raw.tool === "string" ? raw.tool : "tool"
    const error = raw.error && typeof raw.error === "object" ? (raw.error as { message?: unknown }) : undefined
    const result = error?.message ? String(error.message) : raw.result === undefined ? undefined : JSON.stringify(raw.result)

    return {
        type: "tool",
        toolUseId: typeof raw.id === "string" ? raw.id : `mcp-tool-${messageIndex}`,
        toolName: `MCP: ${server}.${tool}`,
        input: raw.arguments,
        result,
        isError: Boolean(error) || raw.status === "failed",
        messageIndices: [messageIndex, undefined],
    }
}

function buildCodexNativeToolGroup(event: CodexRawJsonEvent, messageIndex: number): ToolGroup | null {
    const payload = event.raw.payload && typeof event.raw.payload === "object" ? (event.raw.payload as Record<string, unknown>) : event.raw
    const toolUseId = stringValue(payload.call_id) ?? stringValue(payload.id) ?? `${event.original_type ?? "tool"}-${messageIndex}`

    if (event.original_type === "web_search_call" || event.original_type === "web_search_end") {
        const action = recordValue(payload.action)
        return {
            type: "tool",
            toolUseId,
            toolName: "WebSearch",
            input: action ?? { query: payload.query },
            result: event.original_type === "web_search_end" ? "Search completed" : undefined,
            isError: false,
            messageIndices: [messageIndex, undefined],
        }
    }

    if (event.original_type === "mcp_tool_call_end") {
        const invocation = recordValue(payload.invocation)
        const server = stringValue(invocation?.server) ?? "mcp"
        const tool = stringValue(invocation?.tool) ?? "tool"
        const result = payload.result === undefined ? undefined : JSON.stringify(payload.result)
        return {
            type: "tool",
            toolUseId,
            toolName: `MCP: ${server}.${tool}`,
            input: invocation?.arguments,
            result,
            isError: Boolean(recordValue(payload.result)?.Err),
            messageIndices: [messageIndex, undefined],
        }
    }

    if (event.original_type === "tool_search_call" || event.original_type === "tool_search_output") {
        return {
            type: "tool",
            toolUseId,
            toolName: "ToolSearch",
            input: payload.arguments ?? { execution: payload.execution },
            result: event.original_type === "tool_search_output" && payload.tools !== undefined ? JSON.stringify(payload.tools) : undefined,
            isError: payload.status === "failed",
            messageIndices: [messageIndex, undefined],
        }
    }

    if (event.original_type === "image_generation_call" || event.original_type === "image_generation_end") {
        const input = {
            status: payload.status,
            revised_prompt: payload.revised_prompt,
            saved_path: payload.saved_path,
        }
        const result =
            typeof payload.saved_path === "string"
                ? `Saved to ${payload.saved_path}`
                : event.original_type === "image_generation_end"
                  ? "Image generated"
                  : undefined
        return {
            type: "tool",
            toolUseId,
            toolName: "ImageGeneration",
            input,
            result,
            isError: payload.status === "failed",
            messageIndices: [messageIndex, undefined],
        }
    }

    return null
}

/** Convert a CodexItem into the appropriate MessageGroup and push it. */
function pushItemGroup(groups: MessageGroup[], item: CodexItem, messageIndex: number, isPending: boolean): void {
    const rawItem = item as unknown as { type?: string; original_type?: string; raw?: Record<string, unknown> }
    const renderPolicy = rawItem.type && rawItem.type !== "unsupported" ? CODEX_ITEM_RENDER_POLICIES[rawItem.type as RenderableCodexItemType] : undefined

    if (renderPolicy === "tool" && rawItem.type === "web_search") {
        groups.push(buildWebSearchToolGroup(rawItem as Record<string, unknown>, messageIndex))
        return
    }
    if (renderPolicy === "tool" && rawItem.type === "mcp_tool_call") {
        groups.push(buildMcpToolCallGroup(rawItem as Record<string, unknown>, messageIndex))
        return
    }
    if (rawItem.type === "unsupported" && rawItem.original_type === "web_search" && rawItem.raw) {
        groups.push(buildWebSearchToolGroup(rawItem.raw, messageIndex))
        return
    }
    if (rawItem.type === "unsupported" && rawItem.original_type === "mcp_tool_call" && rawItem.raw) {
        groups.push(buildMcpToolCallGroup(rawItem.raw, messageIndex))
        return
    }

    switch (item.type) {
        case "reasoning":
            groups.push({
                type: "thinking",
                text: item.text,
                messageIndex,
            })
            break

        case "agent_message":
            groups.push({
                type: "text",
                text: item.text,
                messageIndex,
            })
            break

        case "command_execution":
            if (pushApplyPatchFileChangeGroups(groups, item, messageIndex, isPending)) break
            groups.push({
                type: "bash",
                toolUseId: item.id,
                command: item.command,
                result: item.aggregated_output || undefined,
                isError: item.exit_code !== null && item.exit_code !== 0,
                isPending: isPending || item.status === "in_progress",
                messageIndices: [messageIndex, undefined],
            })
            break

        case "orchestration":
            pushCodexOrchestrationGroups(groups, item, messageIndex, isPending)
            break

        case "custom_tool_call":
            groups.push({
                type: "tool",
                toolUseId: item.id,
                toolName: item.toolName,
                input: item.input,
                result: item.output || undefined,
                isError: item.status === "failed",
                messageIndices: [messageIndex, undefined],
            })
            break

        case "file_change":
            pushFileChangeGroups(groups, item, messageIndex, isPending)
            break

        case "todo_list": {
            const todos: TodoItem[] = item.items.map((todo) => ({
                content: todo.text,
                status: todo.completed ? "completed" : "pending",
                activeForm: todo.text,
            }))
            groups.push({
                type: "todoWrite",
                toolUseId: item.id,
                todos,
                isError: false,
                isPending,
                messageIndices: [messageIndex, undefined],
            })
            break
        }

        case "error":
            groups.push({
                type: "result",
                subtype: "error_during_execution",
                durationMs: 0,
                totalCostUsd: 0,
                usage: { inputTokens: 0, outputTokens: 0 },
                isError: true,
                errors: [item.message],
                messageIndex,
            })
            break

        case "unsupported":
            groups.push({
                type: "unknown",
                harnessId: "codex",
                label: `Unknown Codex item: ${item.original_type ?? "item"}`,
                originalType: item.original_type,
                raw: item.raw,
                messageIndex,
            })
            break
    }
}

function pushCodexOrchestrationGroups(groups: MessageGroup[], item: CodexOrchestrationItem, messageIndex: number, pending: boolean): void {
    const parsed = parseCodexOrchestration(item.input)
    const calls = parsed?.calls ?? []
    const isPending = pending || item.status === "in_progress"
    const isError = item.status === "failed" || (item.exit_code !== null && item.exit_code !== 0)
    const patchCall = calls.length === 1 && calls[0]?.toolName === "apply_patch" ? calls[0] : undefined

    if (!isPending && !isError && item.exit_code === 0 && patchCall?.patch) {
        const changes = parseApplyPatchChanges(patchCall.patch)
        if (changes.length > 0) {
            pushFileChangeGroups(
                groups,
                {
                    id: item.id,
                    type: "file_change",
                    changes,
                    status: item.status,
                },
                messageIndex,
                false
            )
            return
        }
    }

    const singleCall = calls.length === 1 ? calls[0] : undefined
    const input = staticRecord(singleCall?.input)
    if (singleCall?.toolName === "exec_command" && typeof input?.cmd === "string") {
        pushCodexTerminalGroup(groups, {
            type: "bash",
            toolUseId: item.id,
            command: input.cmd,
            ...(item.nested_output ? { result: item.nested_output } : {}),
            ...(item.session_id !== undefined ? { sessionId: item.session_id } : {}),
            ...(item.duration_ms !== undefined ? { durationMs: item.duration_ms } : {}),
            isError,
            isPending,
            messageIndices: [messageIndex, undefined],
        })
        return
    }

    if (singleCall?.toolName === "write_stdin" && typeof input?.session_id === "number") {
        pushCodexTerminalGroup(groups, {
            type: "bash",
            toolUseId: item.id,
            command: `Terminal session ${input.session_id}`,
            description: "Command output",
            ...(item.nested_output ? { result: item.nested_output } : {}),
            sessionId: input.session_id,
            isContinuation: true,
            ...(item.duration_ms !== undefined ? { durationMs: item.duration_ms } : {}),
            isError,
            isPending,
            messageIndices: [messageIndex, undefined],
        })
        return
    }

    if (singleCall?.toolName === "update_plan") {
        const todos = planTodos(input)
        if (todos) {
            groups.push({
                type: "todoWrite",
                toolUseId: item.id,
                label: "Plan updated",
                todos,
                isError,
                isPending,
                messageIndices: [messageIndex, undefined],
            })
            return
        }
    }

    if (singleCall && singleCall.input !== undefined && singleCall.toolName !== "apply_patch") {
        groups.push({
            type: "tool",
            toolUseId: item.id,
            toolName: codexNestedToolLabel(singleCall.toolName),
            input: singleCall.input,
            ...(item.nested_output ? { result: item.nested_output } : {}),
            ...(item.media?.length ? { media: item.media } : {}),
            isError,
            messageIndices: [messageIndex, undefined],
        })
        return
    }

    groups.push({
        type: "orchestration",
        toolUseId: item.id,
        source: item.input,
        calls: calls.map(({ toolName, detail }) => ({ toolName, ...(detail !== undefined ? { detail } : {}) })),
        result: item.output || undefined,
        isError,
        isPending,
        messageIndices: [messageIndex, undefined],
    })
}

function staticRecord(value: CodexStaticValue | undefined): { [key: string]: CodexStaticValue } | undefined {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? value : undefined
}

function planTodos(input: { [key: string]: CodexStaticValue } | undefined): TodoItem[] | null {
    if (!Array.isArray(input?.plan)) return null
    const todos: TodoItem[] = []
    for (const rawItem of input.plan) {
        const item = staticRecord(rawItem)
        if (!item || typeof item.step !== "string" || !isTodoStatus(item.status)) return null
        todos.push({ content: item.step, activeForm: item.step, status: item.status })
    }
    return todos
}

function isTodoStatus(value: CodexStaticValue | undefined): value is TodoItem["status"] {
    return value === "pending" || value === "in_progress" || value === "completed"
}

function codexNestedToolLabel(toolName: string): string {
    if (toolName === "web__run") return "Web"
    if (toolName === "view_image") return "View image"
    return toolName
}

function pushCodexTerminalGroup(groups: MessageGroup[], group: BashGroup): void {
    if (!group.isContinuation || group.sessionId === undefined) {
        groups.push(group)
        return
    }

    const existing = groups.findLast((candidate): candidate is BashGroup => candidate.type === "bash" && candidate.sessionId === group.sessionId)
    if (!existing) {
        groups.push(group)
        return
    }

    existing.result = [existing.result, group.result].filter((value): value is string => Boolean(value)).join("\n") || undefined
    existing.isError = group.isError
    existing.isPending = group.isPending
    existing.durationMs = (existing.durationMs ?? 0) + (group.durationMs ?? 0) || undefined
    existing.messageIndices[1] = group.messageIndices[0]
}

function pushCodexRawJsonGroup(groups: MessageGroup[], event: CodexRawJsonEvent, messageIndex: number): void {
    if (
        event.original_type === "thread_settings_applied" ||
        event.original_type === "world_state" ||
        event.original_type === "thread_name_updated" ||
        event.original_type === "ghost_snapshot"
    ) {
        return
    }

    if (event.original_type === "agent_reasoning") {
        const payload = recordValue(event.raw.payload)
        const text = stringValue(payload?.text)
        if (text?.trim()) {
            groups.push({ type: "thinking", text, messageIndex })
            return
        }
    }

    if (event.original_type === "compacted" || event.original_type === "context_compacted") {
        groups.push({
            type: "system",
            subtype: "compact_boundary",
            metadata: event.raw,
            messageIndex,
        })
        return
    }

    if (event.original_type === "turn_aborted") {
        const payload = recordValue(event.raw.payload)
        const reason = stringValue(payload?.reason) ?? "Turn aborted"
        groups.push({
            type: "result",
            subtype: "error_during_execution",
            durationMs: 0,
            totalCostUsd: 0,
            usage: { inputTokens: 0, outputTokens: 0 },
            isError: true,
            errors: [reason],
            messageIndex,
        })
        return
    }

    const toolGroup = buildCodexNativeToolGroup(event, messageIndex)
    if (toolGroup) {
        groups.push(toolGroup)
        return
    }

    if (event.original_type === "thread_goal_updated") {
        const priorGoalIndex = groups.findIndex((group) => group.type === "providerNotice" && group.kind === "goal" && group.harnessId === "codex")
        if (priorGoalIndex >= 0) groups.splice(priorGoalIndex, 1)
        groups.push(codexGoalNotice(event, messageIndex))
        return
    }

    if (event.original_type === "thread_rolled_back") {
        const payload = recordValue(event.raw.payload)
        const turns = typeof payload?.num_turns === "number" && Number.isFinite(payload.num_turns) ? payload.num_turns : 1
        groups.push({
            type: "providerNotice",
            harnessId: "codex",
            kind: "rollback",
            title: `Rolled back ${turns} turn${turns === 1 ? "" : "s"}`,
            level: "warning",
            metadata: { turns },
            messageIndex,
        })
        return
    }

    pushCodexUnknownEventGroup(groups, event, messageIndex)
}

function codexGoalNotice(event: CodexRawJsonEvent, messageIndex: number): ProviderNoticeGroup {
    const payload = recordValue(event.raw.payload)
    const goal = recordValue(payload?.goal)
    const status = stringValue(goal?.status) ?? "active"
    const objective = stringValue(goal?.objective)
    const metadata: Record<string, unknown> = { status }
    if (typeof goal?.tokensUsed === "number" && Number.isFinite(goal.tokensUsed)) metadata.tokensUsed = goal.tokensUsed
    if (typeof goal?.timeUsedSeconds === "number" && Number.isFinite(goal.timeUsedSeconds)) metadata.timeUsedSeconds = goal.timeUsedSeconds
    return {
        type: "providerNotice",
        harnessId: "codex",
        kind: "goal",
        title: `Goal ${status.replaceAll("_", " ")}`,
        ...(objective ? { body: objective } : {}),
        level: codexGoalNoticeLevel(status),
        metadata,
        messageIndex,
    }
}

function codexGoalNoticeLevel(status: string): ProviderNoticeGroup["level"] {
    switch (status) {
        case "complete":
            return "success"
        case "blocked":
            return "error"
        case "paused":
            return "warning"
        default:
            return "info"
    }
}

function pushApplyPatchFileChangeGroups(groups: MessageGroup[], item: CodexCommandExecutionItem, messageIndex: number, isPending: boolean): boolean {
    if (item.toolName !== "apply_patch") return false
    const changes = parseApplyPatchChanges(item.input)
    if (changes.length === 0) return false
    pushFileChangeGroups(
        groups,
        {
            id: item.id,
            type: "file_change",
            changes,
            status: item.status,
        },
        messageIndex,
        isPending
    )
    return true
}

interface ApplyPatchChange {
    path: string
    oldPath: string
    kind: string
    bodyLines: string[]
}

interface ApplyPatchHunk {
    heading: string
    lines: string[]
}

function parseApplyPatchChanges(input: string | undefined): CodexFileChangeItem["changes"] {
    if (!input?.startsWith("*** Begin Patch")) return []
    const lines = input.split(/\r?\n/)
    const changes: CodexFileChangeItem["changes"] = []
    let current: ApplyPatchChange | null = null

    const flush = () => {
        if (!current) return
        changes.push({
            path: current.path,
            kind: current.kind,
            diff: buildApplyPatchUnifiedDiff(current),
        })
    }

    for (const line of lines) {
        if (line === "*** End Patch") {
            flush()
            current = null
            continue
        }
        const add = line.match(/^\*\*\* Add File: (.+)$/)
        const del = line.match(/^\*\*\* Delete File: (.+)$/)
        const update = line.match(/^\*\*\* Update File: (.+)$/)
        if (!add && !del && !update) {
            const move = line.match(/^\*\*\* Move to: (.+)$/)
            if (move?.[1] && current) {
                current.path = move[1]
                continue
            }
            if (current && line !== "*** Begin Patch" && line !== "") {
                current.bodyLines.push(line)
            }
            continue
        }
        flush()
        const path = add?.[1] ?? del?.[1] ?? update?.[1] ?? "unknown"
        current = {
            path,
            oldPath: path,
            kind: add ? "add" : del ? "delete" : "update",
            bodyLines: [],
        }
    }
    flush()
    return changes
}

function buildApplyPatchUnifiedDiff(change: ApplyPatchChange): string {
    const oldPath = change.oldPath
    const newPath = change.path
    const output: string[] = [`diff --git a/${oldPath} b/${newPath}`]

    if (change.kind === "add") {
        output.push("new file mode 100644", "--- /dev/null", `+++ b/${newPath}`)
        appendApplyPatchHunks(output, splitApplyPatchHunks(change.bodyLines), { oldStart: 0, newStart: 1 })
        return `${output.join("\n")}\n`
    }

    if (change.kind === "delete") {
        output.push("deleted file mode 100644", `--- a/${oldPath}`, "+++ /dev/null")
        appendApplyPatchHunks(output, splitApplyPatchHunks(change.bodyLines), { oldStart: 1, newStart: 0 })
        return `${output.join("\n")}\n`
    }

    if (oldPath !== newPath) {
        output.push(`rename from ${oldPath}`, `rename to ${newPath}`)
    }
    output.push(`--- a/${oldPath}`, `+++ b/${newPath}`)
    appendApplyPatchHunks(output, splitApplyPatchHunks(change.bodyLines), { oldStart: 1, newStart: 1 })
    return `${output.join("\n")}\n`
}

function splitApplyPatchHunks(lines: string[]): ApplyPatchHunk[] {
    const hunks: ApplyPatchHunk[] = []
    let current: ApplyPatchHunk | null = null

    const flush = () => {
        if (!current) return
        if (current.lines.length > 0) hunks.push(current)
        current = null
    }

    for (const line of lines) {
        if (line === "*** End of File") continue
        if (line.startsWith("@@")) {
            flush()
            current = { heading: getApplyPatchHunkHeading(line), lines: [] }
            continue
        }
        if (!isApplyPatchDiffLine(line)) continue
        if (!current) current = { heading: "", lines: [] }
        current.lines.push(line)
    }

    flush()
    return hunks
}

function appendApplyPatchHunks(output: string[], hunks: ApplyPatchHunk[], starts: { oldStart: number; newStart: number }): void {
    let oldCursor = starts.oldStart
    let newCursor = starts.newStart

    for (const hunk of hunks) {
        const oldCount = hunk.lines.filter((line) => line.startsWith("-") || line.startsWith(" ")).length
        const newCount = hunk.lines.filter((line) => line.startsWith("+") || line.startsWith(" ")).length
        const suffix = hunk.heading ? ` ${hunk.heading}` : ""
        output.push(`@@ -${oldCursor},${oldCount} +${newCursor},${newCount} @@${suffix}`)
        output.push(...hunk.lines)
        oldCursor += oldCount
        newCursor += newCount
    }
}

function getApplyPatchHunkHeading(line: string): string {
    const body = line.slice(2).trim()
    const standardHeader = body.match(/^-\d+(?:,\d+)? \+\d+(?:,\d+)? @@\s*(.*)$/)
    return standardHeader?.[1] ?? body
}

function isApplyPatchDiffLine(line: string): boolean {
    return line.startsWith("+") || line.startsWith("-") || line.startsWith(" ")
}

function pushCodexUnknownEventGroup(groups: MessageGroup[], event: CodexRawJsonEvent, messageIndex: number): void {
    groups.push({
        type: "unknown",
        harnessId: "codex",
        label: `Unknown Codex event: ${event.original_type ?? "event"}`,
        originalType: event.original_type,
        raw: event.raw,
        messageIndex,
    })
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined
}

function pushFileChangeGroups(groups: MessageGroup[], item: CodexFileChangeItem, messageIndex: number, isPendingEvent: boolean): void {
    const status = item.status.toLowerCase()
    const isPending = isPendingEvent || status === "in_progress" || status === "inprogress"
    const isError = status === "failed" || status === "declined"

    const changes = Array.isArray(item.changes) ? item.changes : []

    changes.forEach((change, changeIndex) => {
        groups.push({
            type: "fileChange",
            toolUseId: item.id,
            filePath: change.path,
            kind: change.kind,
            status: item.status,
            diff: change.diff,
            isError,
            isPending,
            messageIndex,
            changeIndex,
        })
    })
}
